import moment from "moment";
import qs from "qs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "src/hooks/useI18n";
import { maskEmail, maskMiddleChars, maskString, ViewYmdFormat } from "src/utils";

interface Props {
  reservation: any;
  facility: any;
  handleReservationDetailModal: (type: string, payload?: any) => void;
  getCurrentTimePosition: number;
  currentPage: number;
  timeSlotsTime: string;
  selectedDate: Date;
}

const ReservationBoxItem = ({
  reservation,
  facility,
  handleReservationDetailModal,
  getCurrentTimePosition,
  currentPage,
  timeSlotsTime,
  selectedDate,
}: Props) => {
  const location = useLocation();
  const { t } = useI18n();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const queryParams: any = qs.parse(location.search, {
    ignoreQueryPrefix: true,
    allowDots: true,
    decoder: (c) => c,
  });

  const cellHeight = 45;

  const getAttendeeLength = (attendees: any[], organizerMemberNo: string) => {
    return attendees.filter((attendee) => attendee.memberNo !== organizerMemberNo)?.length;
  };

  const getStatus = (start: string, end: string) => {
    const now = moment();
    const startTime = moment(start);
    const endTime = moment(end);

    if (now.isBefore(startTime)) {
      return "ready";
    } else if (now.isBetween(startTime, endTime)) {
      return "on";
    } else {
      return "off";
    }
  };

  const getBoxOffsetFromScrollContainer = () => {
    if (!boxRef.current) return 0;

    const scrollContainer = boxRef.current.closest(".reservation-calendar-content-scroll");
    if (!scrollContainer) return 0;

    const containerRect = scrollContainer.getBoundingClientRect();
    const boxRect = boxRef.current.getBoundingClientRect();
    const scrollTop = scrollContainer.scrollTop;

    // 컨테이너 내에서의 실제 위치 (스크롤 포함)
    const relativeTop = boxRect.top - containerRect.top + scrollTop;

    return relativeTop;
  };

  const renderTitle = (reservation: any) => {
    if (queryParams.reservationName === "public") {
      return reservation?.summary ? reservation?.summary : " ";
    } else if (queryParams.reservationName === "restricted") {
      return reservation?.summary ? maskString(reservation?.summary, 3) : " ";
    } else {
      return t("reservation.display.body.private");
    }
  };

  const renderReservationInfo = (reservation: any) => {
    if (queryParams.attendeeInfo === "public") {
      if (!reservation.organizer?.displayName) {
        return `${reservation.organizer?.userEmail} ${
          getAttendeeLength(reservation.attendees, reservation.organizer.memberNo) > 0
            ? "외 " + getAttendeeLength(reservation.attendees, reservation.organizer.memberNo) + "명"
            : ""
        }`;
      }
      return `${reservation.organizer?.displayName} (${reservation.organizer?.userEmail}) ${
        getAttendeeLength(reservation.attendees, reservation.organizer.memberNo) > 0
          ? "외 " + getAttendeeLength(reservation.attendees, reservation.organizer.memberNo) + "명"
          : ""
      }`;
    } else if (queryParams.attendeeInfo === "restricted") {
      if (!reservation.organizer?.displayName) {
        return `${maskEmail(reservation.organizer?.userEmail)} ${
          getAttendeeLength(reservation.attendees, reservation.organizer.memberNo) > 0
            ? "외 " + getAttendeeLength(reservation.attendees, reservation.organizer.memberNo) + "명"
            : ""
        }`;
      }
      return `${maskMiddleChars(reservation.organizer?.displayName)}(${maskEmail(reservation.organizer?.userEmail)}) ${
        getAttendeeLength(reservation.attendees, reservation.organizer.memberNo) > 0
          ? "외 " + getAttendeeLength(reservation.attendees, reservation.organizer.memberNo) + "명"
          : ""
      }`;
    } else {
      return t("reservation.display.body.private");
    }
  };

  // 타이틀 위치 조정 로직
  useEffect(() => {
    if (!boxRef.current) return;

    let isTransitioning = false;

    const checkVisibility = () => {
      if (!boxRef.current || isTransitioning) return;

      // DOM이 완전히 렌더링되었는지 확인
      if (boxRef.current.offsetHeight === 0) {
        setTimeout(checkVisibility, 50);
        return;
      }

      const rect = boxRef.current.getBoundingClientRect();
      const outOfView = rect.top < 280 - 34;

      if (wrapperRef.current) {
        const status = getStatus(reservation.start, reservation.end);
        const shouldAlignToCurrentTime = status === "on" && outOfView;

        if (shouldAlignToCurrentTime) {
          const offsetPosition = getBoxOffsetFromScrollContainer();
          const newTop = getCurrentTimePosition - offsetPosition - 10;

          wrapperRef.current.style.position = "absolute";
          wrapperRef.current.style.top = `${newTop}px`;
          wrapperRef.current.style.zIndex = "100";
          wrapperRef.current.style.width = "calc(100% - 40px)";
        } else {
          wrapperRef.current.style.position = "";
          wrapperRef.current.style.top = "";
          wrapperRef.current.style.zIndex = "";
          wrapperRef.current.style.width = "";
        }
      }
    };

    // RAF를 사용해서 브라우저 렌더링 사이클에 맞춰 실행
    const initialCheck = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          checkVisibility();
        });
      });
    };

    // 초기 체크를 여러 번 실행
    const initialCheckTimeout1 = setTimeout(initialCheck, 50);
    const initialCheckTimeout2 = setTimeout(initialCheck, 200);
    const initialCheckTimeout3 = setTimeout(initialCheck, 500);

    let scrollTimer: ReturnType<typeof setTimeout> | null = null;
    const handleScroll = () => {
      if (scrollTimer) clearTimeout(scrollTimer);
      if (isTransitioning) return;

      // 스크롤 완료 후 체크 (성능 최적화)
      scrollTimer = setTimeout(() => {
        checkVisibility();
      }, 100);
    };

    const scrollContainer = boxRef.current.closest(".reservation-calendar-content-scroll");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    }

    window.addEventListener("resize", handleScroll);

    const slideChangeHandler = () => {
      isTransitioning = true;
      setTimeout(() => {
        isTransitioning = false;
        checkVisibility();
      }, 300);
    };

    const swiperContainer = document.querySelector(".swiper-container");
    if (swiperContainer) {
      swiperContainer.addEventListener("transitionend", slideChangeHandler);
    }

    const slideChangeObserver = new MutationObserver(() => {
      setTimeout(checkVisibility, 100);
    });

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(checkVisibility, 50);
    });

    if (boxRef.current) {
      resizeObserver.observe(boxRef.current);

      if (boxRef.current.closest(".swiper-slide")) {
        slideChangeObserver.observe(boxRef.current.closest(".swiper-slide")!, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }
    }

    return () => {
      clearTimeout(initialCheckTimeout1);
      clearTimeout(initialCheckTimeout2);
      clearTimeout(initialCheckTimeout3);
      if (scrollTimer) clearTimeout(scrollTimer);

      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("resize", handleScroll);

      if (swiperContainer) {
        swiperContainer.removeEventListener("transitionend", slideChangeHandler);
      }

      slideChangeObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [getCurrentTimePosition, currentPage, reservation]);

  // 페이지 변경시 스타일 리셋
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (wrapperRef.current) {
        wrapperRef.current.removeAttribute("style");
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [currentPage]);

  // 예약 시간 계산 (선택된 날짜 기준으로)
  const startTime = moment(reservation.start);
  const endTime = moment(reservation.end);
  const selectedMoment = moment(selectedDate);
  const isTodayReservation = startTime.isSame(selectedMoment, "day") && startTime.format("HH:mm") === timeSlotsTime;
  const isBeforeAndAfter = startTime.isBefore(selectedMoment, "day") && endTime.isAfter(selectedMoment, "day") && timeSlotsTime === "00:00";
  const isBeforeAndToday = startTime.isBefore(selectedMoment, "day") && endTime.isSame(selectedMoment, "day") && timeSlotsTime === "00:00";

  const startToDateMinute = moment(reservation.end).diff(selectedMoment.startOf("day"), "minutes"); // 선택된 날짜 00:00부터 끝나는시점까지의 분

  if ((isTodayReservation || isBeforeAndAfter || isBeforeAndToday) && reservation.status.code === "ACKNOWLEDGED") {
    return (
      <div
        key={reservation.id}
        ref={boxRef}
        className={`reservation-box ${getStatus(reservation?.start, reservation?.end)} ${isBeforeAndAfter ? "full-reservation" : ""} ${
          isBeforeAndToday ? "before-today-reservation" : ""
        }`}
        style={{
          height: isTodayReservation
            ? (reservation?.minutes / 30) * cellHeight || 45 * 3
            : isBeforeAndAfter
            ? 2 * 25 * cellHeight || 45 * 3
            : (startToDateMinute / 30) * cellHeight + cellHeight || 45 * 3,
        }}
        onClick={() => handleReservationDetailModal("open", reservation)}
      >
        <div className="reservation-box-wrap" ref={wrapperRef}>
          <p className="reservation-box-title">{renderTitle(reservation)}</p>
          <p className="reservation-box-first-info">
            {moment(reservation.start).format("HH:mm")} - {moment(reservation.end).format("HH:mm")}
          </p>
          <p className="reservation-box-second-info ellipsis">{renderReservationInfo(reservation)}</p>
          {reservation.minutes / 30 >= 3 && reservation.mbOrganizationName && (
            <p className="reservation-box-third-info">
              {queryParams.companyName === "public"
                ? reservation.mbOrganizationName
                : queryParams.companyName === "restricted"
                ? maskString(reservation.mbOrganizationName, 1)
                : ""}
            </p>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default ReservationBoxItem;
